import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import type { ProcessBlueprint, ProcessBlueprintLog } from '../../../../types/processBlueprint';
import { listGovernanceLogs } from '../../services/governance.service';
import { getBlueprintById } from '../../services/blueprint.service';
import { useVersionCompare } from '../../hooks/useVersionCompare';
import { BlueprintOverviewTab } from './tabs/BlueprintOverviewTab.tsx';
import { GovernanceLogTab } from './tabs/GovernanceLogTab.tsx';
import { UsageStatsTab } from './tabs/UsageStatsTab.tsx';
import { VersionHistoryTab } from './tabs/VersionHistoryTab.tsx';
import { BlueprintPerformanceTab } from './tabs/BlueprintPerformanceTab.tsx';
import { BlueprintStagesTab } from './tabs/BlueprintStagesTab.tsx';
import { PermissionGate } from '../shared/PermissionGate';
import { usePermissions } from '../../hooks/usePermissions';

interface BlueprintDrawerProps {
  open: boolean;
  blueprintId: string | null;
  organizationId: string;
  onClose: () => void;
  mode?: string | null;
  withinHost?: boolean;
}

type TabKey = 'overview' | 'stages' | 'logs' | 'usage' | 'performance' | 'versions' | 'attachments';
type PermissionKey = keyof NonNullable<ReturnType<typeof usePermissions>['permissions']>;
type AsyncState<T> = {
  data: T;
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
};

const tabs: Array<{ key: TabKey; label: string; ability: PermissionKey }> = [
  { key: 'overview', label: 'Overview', ability: 'BLUEPRINT_VIEW' },
  { key: 'stages', label: 'Etapas', ability: 'BLUEPRINT_EDIT' },
  { key: 'logs', label: 'Governança', ability: 'BLUEPRINT_VIEW_LOGS' },
  { key: 'usage', label: 'Uso', ability: 'BLUEPRINT_VIEW' },
  { key: 'performance', label: 'Performance', ability: 'BLUEPRINT_VIEW_PERFORMANCE' },
  { key: 'versions', label: 'Versões', ability: 'BLUEPRINT_VIEW' },
  { key: 'attachments', label: 'Anexos', ability: 'BLUEPRINT_VIEW' },
];

export function BlueprintDrawer({ open, blueprintId, organizationId, onClose, mode }: BlueprintDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [blueprintState, setBlueprintState] = useState<AsyncState<ProcessBlueprint | null>>({ data: null, status: 'idle' });
  const [logsState, setLogsState] = useState<AsyncState<ProcessBlueprintLog[]>>({ data: [], status: 'idle' });
  const [blueprintReloadKey, setBlueprintReloadKey] = useState(0);
  const [logsReloadKey, setLogsReloadKey] = useState(0);
  const [versionsReloadKey, setVersionsReloadKey] = useState(0);
  const { permissions } = usePermissions(organizationId);
  const visibleTabs = useMemo(() => tabs.filter((tab) => permissions?.[tab.ability]), [permissions]);

  useEffect(() => {
    if (!visibleTabs.length) return;
    if (!visibleTabs.find((tab) => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    if (!open || !mode || !visibleTabs.length) return;
    const requestedTab = visibleTabs.find((tab) => tab.key === mode);
    if (requestedTab && requestedTab.key !== activeTab) {
      setActiveTab(requestedTab.key);
    }
  }, [activeTab, mode, open, visibleTabs]);

  useEffect(() => {
    if (!open) {
      setActiveTab('overview');
      setBlueprintState({ data: null, status: 'idle' });
      setLogsState({ data: [], status: 'idle' });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !blueprintId) return;
    let cancelled = false;
    setBlueprintState({ data: null, status: 'loading' });
    setLogsState({ data: [], status: 'idle' });
    getBlueprintById(blueprintId, organizationId)
      .then((bp) => {
        if (cancelled) return;
        if (bp) {
          setBlueprintState({ data: bp, status: 'success' });
        } else {
          setBlueprintState({ data: null, status: 'error', error: 'Blueprint não encontrado.' });
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setBlueprintState({ data: null, status: 'error', error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [open, blueprintId, organizationId, blueprintReloadKey]);

  useEffect(() => {
    if (!open || !blueprintId || activeTab !== 'logs') return;
    let cancelled = false;
    setLogsState({ data: [], status: 'loading' });
    listGovernanceLogs(blueprintId, organizationId)
      .then((logsResult) => {
        if (cancelled) return;
        setLogsState({ data: logsResult, status: 'success' });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLogsState({ data: [], status: 'error', error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [open, blueprintId, organizationId, activeTab, logsReloadKey]);

  const handleBlueprintRetry = useCallback(() => setBlueprintReloadKey((key) => key + 1), []);
  const handleLogsRetry = useCallback(() => setLogsReloadKey((key) => key + 1), []);
  const handleVersionsRetry = useCallback(() => setVersionsReloadKey((key) => key + 1), []);

  const { versions, diff, loading: versionsLoading, error: versionsError } = useVersionCompare(blueprintId, organizationId, {
    enabled: open && activeTab === 'versions',
    reloadKey: versionsReloadKey,
  });

  if (!open || !blueprintId) return null;

  const content = renderContent({
    activeTab,
    blueprintState,
    logsState,
    blueprintId,
    organizationId,
    versions,
    diff,
    versionsLoading,
    versionsError,
    onLogsRetry: handleLogsRetry,
    onBlueprintRetry: handleBlueprintRetry,
    onVersionsRetry: handleVersionsRetry,
    onClose,
  });

  const restrictedFallback = (
    <aside
      className="relative flex h-full w-full max-w-[560px] flex-col border-l border-base-200 bg-base-100 shadow-2xl"
      aria-label="Drawer restrito"
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-[0.12em] text-gray-400">Governança</p>
          <p className="text-sm font-semibold text-gray-700">Acesso restrito</p>
        </div>
        <button type="button" className="rounded-md p-1 text-gray-500 hover:text-gray-900" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className="flex flex-1 items-center justify-center px-6 text-sm text-gray-500">
        Você não possui permissão para visualizar os detalhes deste blueprint.
      </div>
    </aside>
  );

  return (
    <PermissionGate organizationId={organizationId} ability="BLUEPRINT_VIEW" fallback={restrictedFallback}>
      <aside
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-base-200 bg-base-100 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.12em] text-gray-400">Blueprint</p>
            <p className="truncate text-lg font-semibold text-gray-900">
              {blueprintState.status === 'loading' ? 'Carregando blueprint...' : blueprintState.data?.name ?? 'Sem registro'}
            </p>
          </div>
          <button type="button" className="rounded-md p-1 text-gray-500 hover:text-gray-900" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto border-b px-4 py-2 text-sm">
          {visibleTabs.map((tab) => (
            <TabButton key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} label={tab.label} />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">{content}</div>
      </aside>
    </PermissionGate>
  );
}

function renderContent({
  activeTab,
  blueprintState,
  logsState,
  blueprintId,
  organizationId,
  versions,
  diff,
  versionsLoading,
  versionsError,
  onLogsRetry,
  onBlueprintRetry,
  onVersionsRetry,
  onClose,
}: {
  activeTab: TabKey;
  blueprintState: AsyncState<ProcessBlueprint | null>;
  logsState: AsyncState<ProcessBlueprintLog[]>;
  blueprintId: string;
  organizationId: string;
  versions: ReturnType<typeof useVersionCompare>['versions'];
  diff: ReturnType<typeof useVersionCompare>['diff'];
  versionsLoading: boolean;
  versionsError?: string;
  onLogsRetry: () => void;
  onBlueprintRetry: () => void;
  onVersionsRetry: () => void;
  onClose: () => void;
}) {
  if (blueprintState.status === 'loading') {
    return <DrawerSkeleton />;
  }

  if (blueprintState.status === 'error') {
    return <ErrorBlock message={blueprintState.error ?? 'Não foi possível carregar o blueprint.'} onRetry={onBlueprintRetry} />;
  }

  if (!blueprintState.data) {
    return <EmptyBlock message="Nenhum blueprint selecionado." />;
  }

  switch (activeTab) {
    case 'overview':
      return <BlueprintOverviewTab blueprint={blueprintState.data} onClose={onClose} />;
    case 'stages':
      return (
        <BlueprintStagesTab
          blueprint={blueprintState.data}
          organizationId={organizationId}
          onStagesUpdated={onBlueprintRetry}
        />
      );
    case 'logs':
      if (logsState.status === 'loading') return <DrawerSkeleton rows={4} />;
      if (logsState.status === 'error') {
        return <ErrorBlock message={logsState.error ?? 'Falha ao carregar logs.'} onRetry={onLogsRetry} />;
      }
      return <GovernanceLogTab logs={logsState.data} />;
    case 'usage':
      return <UsageStatsTab blueprintId={blueprintId} organizationId={organizationId} />;
    case 'performance':
      return <BlueprintPerformanceTab blueprint={blueprintState.data} organizationId={organizationId} />;
    case 'versions':
      if (versionsLoading) return <DrawerSkeleton rows={3} />;
      if (versionsError) {
        return <ErrorBlock message={versionsError} onRetry={onVersionsRetry} />;
      }
      return <VersionHistoryTab versions={versions} diff={diff} />;
    case 'attachments':
    default:
      return <EmptyBlock message="Nenhum anexo disponível." />;
  }
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`rounded-sm px-3 py-1 text-xs font-medium transition-colors ${active ? 'bg-purple-600 text-white dark:bg-purple-500/30 dark:text-purple-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function DrawerSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={idx} className="h-4 rounded bg-base-200" />
      ))}
    </div>
  );
}

function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800/40 dark:bg-red-500/10 dark:text-red-300">
      <p>{message}</p>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-800/40 dark:text-red-300 dark:hover:bg-red-500/15"
        onClick={onRetry}
      >
        <RefreshCw size={14} />
        <span>Tentar novamente</span>
      </button>
    </div>
  );
}

function EmptyBlock({ message }: { message: string }) {
  return <div className="rounded-md border border-dashed border-gray-200 p-4 text-sm text-gray-500">{message}</div>;
}
